import React, { ReactNode } from 'react';
import { Stack, StackProps, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { FILE_TYPE_LABEL, formatDateTime, formatFileSize } from '../model/formatters';
import type { CampaignKnowledgeBaseFile } from '../model/types';

type Props = {
  file: CampaignKnowledgeBaseFile;
  isAvailable: boolean;
  headerActions?: ReactNode;
  children?: ReactNode;
  stackProps?: StackProps;
};

export const KnowledgeBaseFileDetails: React.FC<Props> = ({
  file,
  isAvailable,
  headerActions,
  children,
  stackProps,
}) => {
  const { t } = useTranslation();
  const title = (
    <Typography variant="subtitle1" fontWeight={600} noWrap title={file.fileName}>
      {file.fileName}
    </Typography>
  );

  return (
    <Stack gap={1.25} {...stackProps}>
      {headerActions ? (
        <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
          {title}
          {headerActions}
        </Stack>
      ) : (
        title
      )}
      <Typography variant="body2" color="text.secondary">
        {t('campaignPage.knowledgeBase.details.uploadDate')}: {formatDateTime(file.createdAt)}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {t('campaignPage.knowledgeBase.details.type')}: {FILE_TYPE_LABEL}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {t('campaignPage.knowledgeBase.details.size')}: {formatFileSize(file.size)}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {t('campaignPage.knowledgeBase.details.chunks')}: {file.indexedChunksCount}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {t('campaignPage.knowledgeBase.details.pages')}: {file.pageCount}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {t('campaignPage.knowledgeBase.details.status')}:{' '}
        {t(`campaignPage.knowledgeBase.status.${file.status}`)}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {t('campaignPage.knowledgeBase.details.availability')}:{' '}
        {t(`campaignPage.knowledgeBase.availability.${isAvailable ? 'enabled' : 'disabled'}`)}
      </Typography>
      {children}
    </Stack>
  );
};
